/**
 * BLRRD pixel-layer data model — Phase 4.A.layers.1.
 *
 * A pixel layer is an opaque Float32 RGBA buffer in ACEScg-linear, with
 * an optional single-channel mask, an opacity scalar, a blend mode, and
 * a position offset. Layers stack bottom-to-top above the kernel-rendered
 * base buffer; the compositor evaluates them in order using the Porter-
 * Duff "over" operator (W3C Compositing-and-Blending Level 1 §6, 1984
 * SIGGRAPH original).
 *
 * Provenance: this module is implemented clean-room from public W3C/PDF
 * specs and the Porter-Duff paper. No GIMP / Photoshop / Krita source
 * was consulted.
 *
 * Contract:
 *   • rgba is Float32 RGBA, length = w * h * 4, ACEScg-linear,
 *     NON-PREMULTIPLIED alpha. Pre-multiplication happens locally inside
 *     the compositor inner loop only.
 *   • opacity is a scalar in [0, 1]. The UI may report 0..100; clamp in
 *     the legacy editor before writing to the model.
 *   • blendMode is one of BLEND_MODE_LIST values (see blend-modes.js).
 *   • mask is { rgba: Float32Array(w*h), w, h } | null. Single-channel
 *     [0, 1] coverage map; multiplied into the layer's alpha at composite
 *     time. Mask w/h must match layer w/h in this wave; future selections
 *     may add independent mask dimensions.
 *   • offsetX/offsetY position the layer's top-left corner relative to
 *     the canvas. Layers may extend beyond canvas bounds; samples outside
 *     the layer rectangle are ignored (transparent).
 */

/**
 * The set of recognised layer kinds. v1 only ships `pixel`; future waves
 * formalise `param` for the legacy parametric overlays and `adjustment`
 * for non-destructive correction layers.
 */
export const LAYER_KIND_PIXEL = 'pixel';

/** Default blend mode for new pixel layers. */
export const DEFAULT_BLEND_MODE = 'normal';

/** Default opacity for new pixel layers. */
export const DEFAULT_OPACITY = 1;

/* Module-local stable-id generator. Not cryptographic — we just need
 * within-session uniqueness so the UI can key layer rows. */
let _layerIdCounter = 0;
function nextLayerId() {
    _layerIdCounter += 1;
    return `pl-${Date.now().toString(36)}-${_layerIdCounter.toString(36)}`;
}

/**
 * Construct a pixel layer. The rgba / mask buffers, if supplied, are
 * adopted by reference (not copied) — callers expecting immutability
 * should clone before passing in.
 *
 * @param {object} opts
 * @param {string} [opts.name='Layer']
 * @param {number} opts.w                 — required, > 0
 * @param {number} opts.h                 — required, > 0
 * @param {number} [opts.opacity=1]       — [0, 1]
 * @param {string} [opts.blendMode='normal']
 * @param {Float32Array} [opts.rgba]      — w*h*4; allocated zero-filled if absent
 * @param {{ rgba: Float32Array, w: number, h: number }|null} [opts.mask=null]
 * @param {number} [opts.offsetX=0]
 * @param {number} [opts.offsetY=0]
 * @param {boolean} [opts.visible=true]
 * @param {string} [opts.id]              — caller-provided stable id; auto otherwise
 * @returns {object} pixel-layer record
 */
export function createPixelLayer(opts) {
    if (!opts || typeof opts !== 'object') {
        throw new Error('createPixelLayer: opts required');
    }
    const w = opts.w | 0;
    const h = opts.h | 0;
    if (w <= 0 || h <= 0) {
        throw new Error(`createPixelLayer: w/h must be positive (got ${opts.w}, ${opts.h})`);
    }
    const expectedLen = w * h * 4;
    let rgba = opts.rgba;
    if (rgba == null) {
        rgba = new Float32Array(expectedLen);
    } else if (!(rgba instanceof Float32Array) || rgba.length !== expectedLen) {
        throw new Error(`createPixelLayer: rgba must be Float32Array length ${expectedLen}`);
    }
    let mask = opts.mask;
    if (mask !== null && mask !== undefined) {
        if (!mask.rgba || !(mask.rgba instanceof Float32Array)) {
            throw new Error('createPixelLayer: mask.rgba must be Float32Array');
        }
        const mw = mask.w | 0, mh = mask.h | 0;
        if (mw <= 0 || mh <= 0 || mask.rgba.length !== mw * mh) {
            throw new Error('createPixelLayer: mask dims invalid');
        }
        /* Codex 4.A.layers.1 STOP-2 — mask dimensions must match the
         * layer's own dimensions in this wave (the compositor samples
         * the mask via the same (lx, ly) coordinates as the rgba
         * buffer). Future selections may add independent mask
         * resolutions; until then, fail loudly so a mismatched mask
         * never silently propagates out-of-bounds reads. */
        if (mw !== w || mh !== h) {
            throw new Error(
                `createPixelLayer: mask dims (${mw}×${mh}) must equal layer dims (${w}×${h})`,
            );
        }
    } else {
        mask = null;
    }
    const opacity = clamp01(Number.isFinite(opts.opacity) ? opts.opacity : DEFAULT_OPACITY);
    const blendMode = typeof opts.blendMode === 'string' ? opts.blendMode : DEFAULT_BLEND_MODE;
    return {
        id: opts.id || nextLayerId(),
        kind: LAYER_KIND_PIXEL,
        name: typeof opts.name === 'string' && opts.name.length > 0 ? opts.name : 'Layer',
        visible: opts.visible !== false,
        opacity,
        blendMode,
        rgba,
        w, h,
        offsetX: Number.isFinite(opts.offsetX) ? opts.offsetX | 0 : 0,
        offsetY: Number.isFinite(opts.offsetY) ? opts.offsetY | 0 : 0,
        mask,
    };
}

/**
 * Discriminator helper. Future waves may add `param` / `adjustment` kinds;
 * this lets the compositor filter to just pixel layers without leaking
 * the legacy `E.layers` parametric overlays into the pixel-compositor.
 */
export function isPixelLayer(layer) {
    return !!layer && layer.kind === LAYER_KIND_PIXEL;
}

/**
 * Canonical empty pixel-layer stack. Returned by `defaultLayers()`; used
 * as the seed for `E.pixelLayers` at editor boot.
 */
export function defaultLayers() {
    return [];
}

/**
 * True when the layer stack is a no-op against the kernel-rendered base:
 *   • empty, OR
 *   • a single visible pixel layer at opacity=1, blendMode='normal',
 *     no mask, offset (0,0), AND that single layer has full coverage
 *     across the canvas (w/h matching).
 *
 * NB: even a "normal at opacity 1" layer changes the canvas when it
 * carries different pixel content from the base; "identity" here means
 * "the compositor itself would do no work" — the ref-passthrough fast
 * path is safe when the base buffer IS the final output. In practice
 * the only way an identity-stack triggers is when no pixel layers
 * have been added; the W/H check guards against the empty-allocation
 * single-layer case being mistaken for non-identity content.
 *
 * The compositor uses this to short-circuit so the pixel-hash + worker-
 * parity baselines stay byte-identical when no pixel layers exist.
 *
 * @param {Array<object>} layers
 * @returns {boolean}
 */
export function isIdentityStack(layers) {
    if (!Array.isArray(layers)) return true;
    if (layers.length === 0) return true;
    /* Any visible layer triggers compositor work. We deliberately do NOT
     * try to detect "single normal-opacity-1 layer" as identity, because
     * its pixel content can differ from the base and would still need
     * compositing to actually paint. The cheap check is "no visible
     * pixel layers". */
    for (let i = 0; i < layers.length; i++) {
        const l = layers[i];
        if (!isPixelLayer(l)) continue;
        if (l.visible !== false) return false;
    }
    return true;
}

/**
 * Add a fresh white (1.0) coverage mask to a pixel layer. Returns a new
 * layer object — does NOT mutate the input (immutability invariant from
 * the user's coding-style rules + the legacy editor's `... old, ...patch`
 * pattern in updatePixelLayer).
 *
 * If the layer already has a mask, this overwrites it with a fresh
 * white-filled buffer (semantically "reset the mask"). Use
 * `clearLayerMask` for the same effect via a more explicit name.
 *
 * @param {object} layer
 * @returns {object} new layer with `.mask = { rgba: white, w, h }`
 */
export function addLayerMask(layer) {
    if (!isPixelLayer(layer)) {
        throw new Error('addLayerMask: not a pixel layer');
    }
    const w = layer.w | 0, h = layer.h | 0;
    if (w <= 0 || h <= 0) {
        throw new Error('addLayerMask: invalid layer dims');
    }
    const rgba = new Float32Array(w * h);
    rgba.fill(1);
    return { ...layer, mask: { rgba, w, h } };
}

/**
 * Remove the layer's mask entirely. Returns a new layer object with
 * `.mask = null` (the canonical "no mask" representation per the
 * compositor's null-check in compositeLayer).
 *
 * @param {object} layer
 * @returns {object} new layer with `.mask = null`
 */
export function removeLayerMask(layer) {
    if (!isPixelLayer(layer)) {
        throw new Error('removeLayerMask: not a pixel layer');
    }
    if (layer.mask === null || layer.mask === undefined) {
        /* Already maskless — return the input unchanged. Callers that
         * compare object identity (e.g. memoised UI) benefit. */
        return layer;
    }
    return { ...layer, mask: null };
}

/**
 * Clear the layer's mask to white (full coverage) in place. If no mask
 * exists, this is equivalent to addLayerMask. The returned mask buffer
 * is a fresh Float32Array — callers that share buffers must not assume
 * the old buffer was reused.
 *
 * @param {object} layer
 * @returns {object} new layer with `.mask.rgba` filled with 1.0
 */
export function clearLayerMask(layer) {
    if (!isPixelLayer(layer)) {
        throw new Error('clearLayerMask: not a pixel layer');
    }
    const w = layer.w | 0, h = layer.h | 0;
    const rgba = new Float32Array(w * h);
    rgba.fill(1);
    return { ...layer, mask: { rgba, w, h } };
}

/**
 * Invert the layer's mask: every coverage value c becomes (1 − c). If
 * no mask exists, this is interpreted as "invert the implicit full-
 * coverage mask" → a fresh all-zero mask. The returned mask buffer is
 * a new Float32Array.
 *
 * @param {object} layer
 * @returns {object} new layer with `.mask.rgba[i] = 1 − old[i]`
 */
export function invertLayerMask(layer) {
    if (!isPixelLayer(layer)) {
        throw new Error('invertLayerMask: not a pixel layer');
    }
    const w = layer.w | 0, h = layer.h | 0;
    const out = new Float32Array(w * h);
    if (layer.mask && layer.mask.rgba && layer.mask.rgba.length === w * h) {
        const src = layer.mask.rgba;
        for (let i = 0; i < out.length; i++) {
            /* Mirror clamp01's sanitiser — NaN / Infinity → 0 inverted = 1. */
            const v = src[i];
            const sane = Number.isFinite(v) ? (v <= 0 ? 0 : (v >= 1 ? 1 : v)) : 0;
            out[i] = 1 - sane;
        }
    }
    /* If no prior mask, out is already all-zeros (invert of all-ones). */
    return { ...layer, mask: { rgba: out, w, h } };
}

/**
 * Filter the layer list to just the pixel kind, preserving order. Used
 * by `buildPipelineContext` and the compositor to keep the parametric
 * `E.layers` overlay system out of the pixel pipeline.
 */
export function selectPixelLayers(layers) {
    if (!Array.isArray(layers)) return [];
    const out = [];
    for (let i = 0; i < layers.length; i++) {
        if (isPixelLayer(layers[i])) out.push(layers[i]);
    }
    return out;
}

/**
 * Clamp a value into [0, 1]. Used at the model boundary for opacity /
 * mask coverage. NaN / Infinity → 0 (safer than silently passing through
 * NaN, which would poison the compositor's alpha math).
 */
export function clamp01(v) {
    if (!Number.isFinite(v)) return 0;
    if (v <= 0) return 0;
    if (v >= 1) return 1;
    return v;
}
